import queryString from "query-string";
import React from "react";
import { useLocation, useParams } from "react-router";
import { ApplicationType } from "@domain/application/application";
import { CountryCode } from "@domain/currentProfile/countryCode";
import { GroupType } from "@domain/group/group";
import { ContentLicense } from "@domain/licence/contentLicense";
import { LivecourseType } from "@domain/livecourse/livecourse";
import { Category } from "@domain/lms/category/category";
import { ContentLanguage } from "@domain/lms/contentLanguage";
import { ContentLevel } from "@domain/lms/contentLevel";
import { CourseDuration, CourseProvider } from "@domain/lms/course/course";
import { OrganizationSubType, OrganizationType } from "@domain/organization/organizationType";
import { Scoring } from "@domain/ranking/ranking";
import { SearchDomain } from "@domain/search/searchDomain";
import { FullPage } from "@ui/components/page";
import { ApplicationEditorPage } from "@ui/pages/application/applicationEditorPage/applicationEditorPage";
import { ApplicationSheetPage } from "@ui/pages/application/applicationEditorPage/applicationSheet/applicationSheet";
import { CarouselPage } from "@ui/pages/carousel/carouselPage";
import { FirebaseActionPage } from "@ui/pages/firebase/firebaseActionPage";
import { GroupEditorPage } from "@ui/pages/group/groupEditorPage/groupEditorPage";
import { GroupPage } from "@ui/pages/group/groupPage";
import { ShareroomPage } from "@ui/pages/group/shareRoomPage";
import Chatmessage from "@ui/pages/Chatmessage";
import { GroupLivecourseEditPage, OrganizationLivecourseEditPage } from "@ui/pages/livecourses/livecoursesEditorPage";
import { LiveGroupcoursesInfos } from "@ui/pages/livecourses/livecoursesInfos/liveGroupcoursesInfos";
import { LiveOrganizationcoursesInfos } from "@ui/pages/livecourses/livecoursesInfos/liveOrganizationcoursesInfos";
import { ArticleEditorPage } from "@ui/pages/lms/article/articleEditorPage/articleEditorPage";
import { ArticlePage } from "@ui/pages/lms/article/articlePage/articlePage";
import { CourseContentPage } from "@ui/pages/lms/courseContent/courseContentPage";
import { CourseEditorPage } from "@ui/pages/lms/courseEditorPage/courseEditorPage";
import { CoursePage } from "@ui/pages/lms/coursePage/coursePage";
import { QuizEditorPage } from "@ui/pages/lms/quiz/quizEditorPage/quizEditorPage";
import { QuizViewPage } from "@ui/pages/lms/quiz/quizViewPage/quizViewPage";
import { NotFoundPage } from "@ui/pages/notFoundPage";
import { OrganizationPage } from "@ui/pages/organizationPage/organizationPage";
import { PostPage } from "@ui/pages/postPage/postPage";
import { ProfilePage } from "@ui/pages/profilePage/profilePage";
import { SearchPage } from "@ui/pages/searchPage/searchPage";
import { useIsMobile } from "@utils/hooks";
import { CompanyTab, DiscussionTab, GroupTab, InstituteTab, ProfileTab } from "./path";
import { Redirect } from "./redirect";

function idOrSlug(value: string | undefined): { id?: string; slug?: string } {
	if (!value) {
		return {};
	}
	if (value.startsWith("@")) {
		return { id: value.slice(1) };
	}
	return { slug: value };
}

function firstQueryValue(value: string | (string | null)[] | null | undefined): string | undefined {
	if (Array.isArray(value)) {
		return value.find((item): item is string => typeof item === "string");
	}
	return value ?? undefined;
}

export const ArticleByIdRoute: React.FC = () => {
	const { id } = useParams();
	const location = useLocation();
	const { action } = queryString.parse(location.search);
	return <ArticlePage articleId={id ?? ""} action={action as "NEW_INSIGHT_EMAIL" | "NEW_INSIGHT_INSTRUCTIVE_EMAIL"} />;
};

export const ArticleBySlugRoute: React.FC = () => {
	const { slug } = useParams();
	const location = useLocation();
	const { action } = queryString.parse(location.search);
	return (
		<ArticlePage
			articleSlug={slug ?? ""}
			action={action as "NEW_INSIGHT_EMAIL" | "NEW_INSIGHT_INSTRUCTIVE_EMAIL" | "FEEDBACK_INSIGHTS_EMAIL"}
		/>
	);
};

export const CarouselByIdRoute: React.FC = () => {
	const { id } = useParams();
	const location = useLocation();
	const { action } = queryString.parse(location.search);
	return (
		<CarouselPage carouselId={id ?? ""} action={action as "NEW_CAROUSEL_EMAIL" | "NEW_CAROUSEL_INSTRUCTIVE_EMAIL"} />
	);
};

export const CarouselBySlugRoute: React.FC = () => {
	const { slug } = useParams();
	const location = useLocation();
	const { action } = queryString.parse(location.search);
	return (
		<CarouselPage
			carouselSlug={slug ?? ""}
			action={action as "NEW_CAROUSEL_EMAIL" | "NEW_CAROUSEL_INSTRUCTIVE_EMAIL"}
		/>
	);
};

export const ArticleEditorRoute: React.FC = () => {
	const { id } = useParams();
	const location = useLocation();
	const { workspace } = queryString.parse(location.search);
	return <ArticleEditorPage articleId={id === "new" ? undefined : id} workspaceId={firstQueryValue(workspace)} />;
};

export const ProfileRoute: React.FC = () => {
	const { profileKey, tab } = useParams();
	const location = useLocation();
	const { id, slug } = idOrSlug(profileKey);
	const { action } = queryString.parse(location.search);
	if (!id && !slug) {
		return <NotFoundPage />;
	}
	return (
		<ProfilePage
			profileId={id}
			profileSlug={slug}
			tab={tab as ProfileTab | undefined}
			action={action as "trackEmail"}
		/>
	);
};

export const DiscussionRoute: React.FC = () => {
	const { groupKey, tab } = useParams();
	const location = useLocation();
	const { id, slug } = idOrSlug(groupKey);
	const { action } = queryString.parse(location.search);
	if (!id && !slug) {
		return <NotFoundPage />;
	}
	return (
		<GroupPage
			groupId={id}
			groupSlug={slug}
			tab={tab as DiscussionTab | undefined}
			action={
				action as
					| "track_USER_INVITED_TO_SHAREROOM"
					| "track_USER_ACCEPTED_TO_SHAREROOM"
					| "track_USER_REMOVED_FROM_SHAREROOM"
					| "track_USER_INVITED_TO_GROUP"
					| "track_USER_ACCEPTED_TO_GROUP"
					| "track_USER_REMOVED_FROM_GROUP"
			}
		/>
	);
};

export const ShareroomRoute: React.FC = () => {
	const { roomKey, tab, workspaceId } = useParams();
	const location = useLocation();
	const { id, slug } = idOrSlug(roomKey);
	const { action } = queryString.parse(location.search);
	const groupTab = tab as GroupTab | undefined;
	if (!id && !slug) {
		return <NotFoundPage />;
	}
	return (
		<ShareroomPage
			shareroomId={id}
			shareroomSlug={slug}
			tab={groupTab}
			action={action as "tups" | "tupf"}
			workspaceId={groupTab === "workspaces" ? workspaceId : undefined}
		/>
	);
};

export const GroupEditorRoute: React.FC = () => {
	const { id, type } = useParams();
	const location = useLocation();
	const { organizationId } = queryString.parse(location.search);
	return (
		<GroupEditorPage
			groupId={!id || id === "new" ? undefined : id}
			type={type as GroupType}
			organizationId={firstQueryValue(organizationId)}
		/>
	);
};

export const ApplicationEditorRoute: React.FC = () => {
	const { id, type } = useParams();
	return (
		<ApplicationEditorPage
			applicationId={!id || id === "new" || id === "view" ? undefined : id}
			type={type as ApplicationType}
		/>
	);
};

export const ChatmessageRoute: React.FC = () => {
	const { profileId } = useParams();
	const location = useLocation();
	const { channel } = queryString.parse(location.search);
	return <Chatmessage profileId={profileId ?? ""} channel={firstQueryValue(channel) ?? null} />;
};

export const ApplicationSheetRoute: React.FC = () => {
	const { applicationKey } = useParams();
	const { id, slug } = idOrSlug(applicationKey);
	if (!id && !slug) {
		return <NotFoundPage />;
	}
	return <ApplicationSheetPage profileId={id} profileSlug={slug} />;
};

export const CourseEditorRoute: React.FC = () => {
	const { id } = useParams();
	const location = useLocation();
	const { workspace } = queryString.parse(location.search);
	return <CourseEditorPage courseId={id === "new" ? undefined : id} workspaceId={firstQueryValue(workspace)} />;
};

export const CourseByIdRoute: React.FC = () => {
	const { id } = useParams();
	const location = useLocation();
	const { action } = queryString.parse(location.search);
	return <CoursePage courseId={id ?? ""} action={action as "NEW_COURSE_EMAIL" | "FEEDBACK_COURS_EMAIL"} />;
};

export const CourseBySlugRoute: React.FC = () => {
	const { slug } = useParams();
	const location = useLocation();
	const { action } = queryString.parse(location.search);
	return <CoursePage courseSlug={slug ?? ""} action={action as "NEW_COURSE_EMAIL" | "FEEDBACK_COURS_EMAIL"} />;
};

export const CourseContentRoute: React.FC = () => {
	const { id } = useParams();
	const location = useLocation();
	const { chapter } = queryString.parse(location.search);
	const chapterValue = firstQueryValue(chapter);
	const chapterIndex = +(chapterValue || 0) || 0;
	return <CourseContentPage courseId={id ?? ""} chapterIndex={chapterIndex} />;
};

export const QuizEditorRoute: React.FC = () => {
	const { id } = useParams();
	const location = useLocation();
	const { workspace } = queryString.parse(location.search);
	return <QuizEditorPage quizId={id === "new" ? undefined : id} workspaceId={firstQueryValue(workspace)} />;
};

export const SearchRoute: React.FC = () => {
	const location = useLocation();
	const {
		q,
		domain,
		categories,
		levels,
		languages,
		durations,
		providers,
		type,
		subTypes,
		countries,
		license,
		meanFeedbackScore,
		averageFeedback,
	} = queryString.parse(location.search);

	const categoriesArray = (categories as string | null)?.split(",") as Category[] | undefined;
	const levelsArray = (levels as string | null)?.split(",") as ContentLevel[] | undefined;
	const languagesArray = (languages as string | null)?.split(",") as ContentLanguage[] | undefined;
	const durationsArray = (durations as string | null)?.split(",") as CourseDuration[] | undefined;
	const providersArray = (providers as string | null)?.split(",") as CourseProvider[] | undefined;
	const orgTypesArray = (type as string | null)?.split(",") as OrganizationType[] | undefined;
	const orgSubTypesArray = (subTypes as string | null)?.split(",") as OrganizationSubType[] | undefined;
	const countriesArray = (countries as string | null)?.split(",") as CountryCode[] | undefined;
	return (
		<SearchPage
			query={(q as string | null) || ""}
			domain={domain as SearchDomain | undefined}
			categories={categoriesArray}
			levels={levelsArray}
			languages={languagesArray}
			durations={durationsArray}
			providers={providersArray}
			orgTypes={orgTypesArray}
			orgSubTypes={orgSubTypesArray}
			countries={countriesArray}
			license={license as ContentLicense[] | undefined}
			scoring={meanFeedbackScore as Scoring[] | undefined}
			rating={averageFeedback ? parseInt(averageFeedback as string) : undefined}
		/>
	);
};

export const PostRoute: React.FC = () => {
	const { id } = useParams();
	return <PostPage postId={id ?? ""} />;
};

export const QuizRoute: React.FC = () => {
	const { id, source } = useParams();
	return <QuizViewPage quizId={id ?? ""} source={source} />;
};

export const OrganizationRoute: React.FC = () => {
	const { orgKey, tab } = useParams();
	const { id, slug } = idOrSlug(orgKey);
	if (!id && !slug) {
		return <NotFoundPage />;
	}
	return (
		<OrganizationPage organizationId={id} organizationSlug={slug} tab={tab as InstituteTab | CompanyTab | undefined} />
	);
};

export const FirebaseActionRoute: React.FC = () => {
	const location = useLocation();
	const { oobCode, mode } = queryString.parse(location.search);

	if (!oobCode || !mode || !["resetPassword", "verifyEmail", "changeEmail", "recoveryEmail"].includes(`${mode}`)) {
		return <Redirect to="/" />;
	}

	return (
		<FirebaseActionPage
			oobCode={oobCode as string}
			mode={mode as "resetPassword" | "verifyEmail" | "changeEmail" | "recoveryEmail" | "linkNumber"}
		/>
	);
};

export const LiveGroupEditorRoute: React.FC = () => {
	const { type, shareroomId, liveId } = useParams();
	return (
		<GroupLivecourseEditPage
			type={type as LivecourseType}
			shareroomId={shareroomId ?? ""}
			livecourseId={liveId === "new" ? undefined : liveId}
		/>
	);
};

export const LiveOrganizationEditorRoute: React.FC = () => {
	const { type, organizationId, liveId } = useParams();
	return (
		<OrganizationLivecourseEditPage
			type={type as LivecourseType}
			organizationId={organizationId ?? ""}
			livecourseId={liveId === "new" ? undefined : liveId}
		/>
	);
};

export const LiveGroupInfosRoute: React.FC = () => {
	const { livecourseId, shareroomId } = useParams();
	const isMobile = useIsMobile();
	const infos = <LiveGroupcoursesInfos shareroomId={shareroomId ?? ""} livecourseId={livecourseId ?? ""} />;
	return isMobile ? (
		<FullPage noMarginTop style={{ marginTop: 0 }}>
			{infos}
		</FullPage>
	) : (
		infos
	);
};

export const LiveOrganizationInfosRoute: React.FC = () => {
	const { livecourseId, organizationId } = useParams();
	const isMobile = useIsMobile();
	const infos = (
		<LiveOrganizationcoursesInfos organizationId={organizationId ?? ""} livecourseId={livecourseId ?? ""} />
	);
	return isMobile ? (
		<FullPage noMarginTop style={{ marginTop: 0 }}>
			{infos}
		</FullPage>
	) : (
		infos
	);
};
